import React, { useState } from 'react';
import { fromUnixTime } from 'date-fns';
import { useSelector } from 'react-redux';
import { isStudyFinished, isStudyRunning, ListedStudy } from 'helper/studies';
import { Col, Row, Spinner } from 'react-bootstrap';
import Button from 'components/DesignSystem/Button';
import Snackbar from 'components/DesignSystem/Snackbar';
import { useReexecuteMutation } from 'services/api/products/flux';
import { selectUserId, selectUserIsAdmin } from 'redux/reducers/authReducer';

import Accordion from 'components/Accordion';
import usePersonalizedDownload from 'utils/hooks/flux/personalized/usePersonalizedDownload';
import ConfirmationDialogBox from '../../../ConfirmationDialogBox';
import StatusTag from './StatusTag';
import PersonalizedScenario from './Scenario';
import useStudyRowActions from './useStudyRowActions';
import './index.scss';

export default function PersonalizedRow({ study }: { study: ListedStudy }) {
  const [triggerReexecute] = useReexecuteMutation();
  const [openReexecutionPopUp, setOpenReexecutionPopUp] = useState(false);
  const start = fromUnixTime(study.dt_inicio).toLocaleDateString('pt-BR');
  const end = fromUnixTime(study.dt_fim).toLocaleDateString('pt-BR');
  const lastExecution = study.dt_last_execution ? fromUnixTime(study.dt_last_execution).toLocaleString('pt-BR') : 'Não executado';
  const lastResponse = study.dt_last_response ? fromUnixTime(study.dt_last_response).toLocaleString('pt-BR') : 'Não houve resposta';
  const {
    download, isLoading: downloading, disabled: downloadDisabled,
  } = usePersonalizedDownload(study);
  const userId = useSelector(selectUserId);
  const isAdmin = useSelector(selectUserIsAdmin);
  // O backend decide a posse; aqui só não oferecemos a ação a quem não é o dono
  const isOwner = isAdmin || (study.id_user !== null && study.id_user === userId);
  const running = isStudyRunning(study);
  const {
    confirmationDialogBox, feedback, clearFeedback,
    confirmDelete, confirmCancel, deleting, cancelling,
  } = useStudyRowActions(study);
  // Os botões ficam dentro do Accordion.Header: sem stopPropagation o clique abre/fecha a linha
  const stopAnd = (action: () => void) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    action();
  };

  return (
    <Accordion.Item
      eventKey={study.id.toString()}
      className="flux-personalized-row"
    >
      <ConfirmationDialogBox
        open={openReexecutionPopUp}
        dismiss={() => { setOpenReexecutionPopUp(false); }}
        confirm={() => { triggerReexecute(study.id); }}
      >
        <div>
          Você tem certeza de que deseja reenviar este estudo (
          <b>{study.ds_nome_estudo}</b>
          ) ?
        </div>
      </ConfirmationDialogBox>
      {confirmationDialogBox}
      {feedback && (
        <Snackbar
          className="mb-2"
          variant={feedback.variant}
          closeButton
          onClose={clearFeedback}
        >
          {feedback.message}
        </Snackbar>
      )}
      <Accordion.Header variant="nested">
        <Row className="w-100">
          <Col md={4} className="flux-personalized-details">
            <div className="flux-personalized-study">
              {study.ds_nome_estudo}
            </div>
            <div>{study.empresa}</div>
            <div>{study.user}</div>
          </Col>
          <Col md={4}>
            {`${start} -- ${end}`}
          </Col>
          <Col md={4} className="flux-personalized-status">
            <StatusTag study={study} />
            <div className="d-flex flex-row justify-content-between">
              <div>
                <div>
                  {`Última Execução: ${lastExecution}`}
                </div>
                <div>
                  {`Última Resposta: ${lastResponse}`}
                </div>
                <div>
                  {`Número de execuções: ${study.vl_execution_counter}`}
                </div>
              </div>
              {/* Flex, não <Col md={1}>: a coluna, mais estreita que o botão, cobria o anterior */}
              <div className="study-row-actions d-flex flex-row flex-wrap align-items-center gap-2 me-3">
                <Button
                  onClick={stopAnd(() => setOpenReexecutionPopUp(true))}
                  className="flux-personalized-button"
                  icon="send"
                  small
                  id={`reruns-study-${study.id}`}
                  title="Execute o estudo novamente"
                />
                <Button
                  onClick={stopAnd(download)}
                  // icon-only no loading: o spinner troca o ícone sem mudar a largura
                  className={`flux-personalized-button${downloading ? ' icon-only' : ''}`}
                  disabled={downloadDisabled || downloading}
                  icon={downloading ? undefined : 'download'}
                  small
                  id={`download-action-${study.id}`}
                  title={downloading ? 'Preparando o arquivo…' : 'Baixe o arquivo de estudo'}
                >
                  {downloading && <Spinner size="sm" />}
                </Button>
                {isOwner && !isStudyFinished(study) && (
                  <Button
                    onClick={stopAnd(confirmCancel)}
                    className="flux-personalized-button"
                    disabled={cancelling}
                    icon="close"
                    small
                    id={`cancel-study-${study.id}`}
                    title="Cancele a execução do estudo"
                  />
                )}
                {isOwner && (
                  <Button
                    onClick={stopAnd(confirmDelete)}
                    className="flux-personalized-button"
                    disabled={running || deleting}
                    icon="trash"
                    small
                    id={`delete-study-${study.id}`}
                    title={running ? 'Cancele o estudo antes de removê-lo' : 'Remova o estudo'}
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Accordion.Header>
      <Accordion.Body>
        {study.cenarios.map((scenario) => (
          <PersonalizedScenario
            scenario={scenario}
            key={scenario.id.toString()}
          />
        ))}
      </Accordion.Body>
    </Accordion.Item>
  );
}
